import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Sheet } from '@/nova/components/Sheet';
import { keepTabIn, useDialogFocus } from '@/nova/components/Sheet/useDialogFocus';
import { Button, Text, LockVeil, type IconName } from '../../../../components';
import { cleanAmountInput, currencySymbol, formatAmountDisplay, formatAmountWithCode, formatMoney } from '../../../../utils/money';
import { ALL_ACQUISITION_AFTS, acquisitionAftFor, isAcquisitionChannelEnabled } from '../../../../config/acquisitionChannels';
import { useAcquisitionChannelGate } from '../../../../hooks/useAcquisitionChannelGate';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchAcquisitionBudget,
  saveAcquisitionBudget,
  selectAcquisitionBudget,
} from '@/store/slices/acquisitionBudget';
import { fetchOwnerWallets, resolveMainWalletForCurrency } from '@/store/slices/wallet';
import { selectSelectedCasinoCurrency } from '@/store/slices/casino-instance';
import { useAcquisitionChannelFigures } from '../../useAcquisitionChannelFigures';
import type { ChannelFigure } from '../../channel-figures';
import { type Channel, type ChannelMode, computeAllocation, withRealCpa } from './allocation';
import { assessTopupFunding } from './topup-funding';
import { useDepositSync } from './useDepositSync';
import { DepositModal } from '../../../wallet/components/DepositModal';
import ChannelGlyph from './ChannelGlyph';
import './budget.scss';

/* ---------- types ---------- */
/* `Channel` / `ChannelMode` / `AllocationResult` + the allocation engine live in `./allocation`. */

type AdBudgetLimitProps = {
  styleText?: string;
  className?: string;
  onCloseBudgetLimits?: () => void;
};

/* ---------- constants ---------- */

/**
 * Per-channel budget-split keys: the acquisition-channel `aft` literals shared with
 * the Owners Hub Stats API (`web` · `app` · `crypto` · `video`; see
 * `pages/home/stats-mapping.ts`). These are exactly the keys the acquisition-budget
 * API expects under `budget_details`, so the channel `id`s above MUST stay in lockstep
 * with them. See `docs/api.owners-hub.acquisition-budget.md`.
 */
const BUDGET_CHANNEL_IDS = ALL_ACQUISITION_AFTS;

/** Per-channel icon (shared Icon set) for the gated-channel lock veil — maps the budget
 *  `aft` ids to the nova Icon names. */
const BUDGET_CHANNEL_ICON: Record<string, IconName> = {
  web: 'globe',
  app: 'mobile',
  crypto: 'crypto',
  video: 'video',
};

const LUMIO_ICON_SRC = 'https://micros-t.b-cdn.net/lumio-icon-1781265880291.png';

// Every channel defaults to Lumio-managed (`mode: 'lumio'`): automatic budget
// allocation is the out-of-the-box behaviour; the user opts a channel out to set a
// manual limit. The chosen mode is now persisted per channel via the `<id>_managed`
// keys in `budget_details` (see `buildBudgetDetails`) and restored on load (see the
// hydration effect), so this default only applies to a brand with no saved budget yet.
// A factory (not a module const) so each channel's `enabled` is resolved at mount, off the
// live gate — a stale module-load snapshot would miss a runtime reveal-flag flip.
const makeInitialChannels = (): Channel[] => [
  {
    id: 'web',
    name: 'Web Networks',
    color: '#22d3ee',
    icon: 'globe',
    cpa: 8.2,
    spent: 42,
    mode: 'lumio',
    manualLimit: 50,
    lumioReason: '',
    enabled: isAcquisitionChannelEnabled('web'),
  },
  {
    id: 'app',
    name: 'App Networks',
    color: '#ff6200',
    icon: 'app',
    cpa: 18.5,
    spent: 30,
    mode: 'lumio',
    manualLimit: 30,
    lumioReason: '',
    enabled: isAcquisitionChannelEnabled('app'),
  },
  {
    id: 'crypto',
    name: 'Crypto Sites',
    color: '#b700ff',
    icon: 'btc',
    cpa: 24.3,
    spent: 14,
    mode: 'lumio',
    manualLimit: 20,
    lumioReason: '',
    enabled: isAcquisitionChannelEnabled('crypto'),
  },
  {
    id: 'video',
    name: 'Short Videos',
    color: '#fff204',
    icon: 'video',
    cpa: 11.4,
    spent: 38,
    mode: 'lumio',
    manualLimit: 40,
    lumioReason: '',
    enabled: isAcquisitionChannelEnabled('video'),
  },
];

/* ---------- top-up tab (figma node 6039:32472) ---------- */

/** Fallback top-up chips shown only before the ads-budget GET resolves (node `6039:32481`);
 *  once it loads, the chips scale with the available budget (see `topupPresets`). */
const TOPUP_PRESETS_FALLBACK = [50, 100, 250];

/** Clean rounding/increment step that grows with the figure's magnitude. */
const niceStep = (value: number): number => (value < 100 ? 5 : value < 500 ? 25 : value < 2000 ? 50 : 100);

/** Round a money figure to a clean step that grows with its magnitude, so the
 *  budget-scaled suggestions read tidily (e.g. $250, not $237). */
const roundToNice = (value: number): number => {
  if (value <= 0) return 0;
  const step = niceStep(value);
  return Math.max(step, Math.round(value / step) * step);
};

/** Round a daily-limit figure UP to the nearest $5 — keeps every suggestion divisible
 *  by 5 (and never 0). Rounding up means the budget lasts a hair under the target days. */
const roundUpToFive = (value: number): number => Math.max(5, Math.ceil(value / 5) * 5);

/* node `6420:17318`: pink→cyan Lumio suggestion sparkle */
const LumioSparkle = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.29937 1.21586C6.83656 -0.35619 9.0087 -0.4038 9.64561 1.07303L9.69951 1.21676L10.4245 3.33678C10.5906 3.82298 10.8591 4.2679 11.2118 4.64152C11.5645 5.01514 11.9932 5.30878 12.469 5.50262L12.664 5.57539L14.784 6.29943C16.3561 6.83662 16.4037 9.00875 14.9277 9.64565L14.784 9.69955L12.664 10.4245C12.1776 10.5905 11.7325 10.8589 11.3587 11.2116C10.9849 11.5644 10.6912 11.9931 10.4972 12.4691L10.4245 12.6631L9.70041 14.784C9.16321 16.3561 6.99107 16.4037 6.35506 14.9277L6.29937 14.784L5.57532 12.664C5.40929 12.1776 5.14087 11.7325 4.78816 11.3588C4.43545 10.985 4.00666 10.6912 3.53074 10.4973L3.3367 10.4245L1.21667 9.70045C-0.356294 9.16326 -0.403905 6.99113 1.07293 6.35512L1.21667 6.29943L3.3367 5.57539C3.8229 5.40925 4.26782 5.14078 4.64145 4.78808C5.01507 4.43537 5.30871 4.00665 5.50256 3.53082L5.57532 3.33678L6.29937 1.21586Z"
      fill="url(#tu-sparkle-grad)"
      fillOpacity="0.75"
    />
    <defs>
      <linearGradient id="tu-sparkle-grad" x1="3.25783" y1="2.83373" x2="16" y2="8" gradientUnits="userSpaceOnUse">
        <stop stopColor="#FF00D4" />
        <stop offset="1" stopColor="#00E0FF" />
      </linearGradient>
    </defs>
  </svg>
);

const DEBUG = true; // set to false to disable debug logs

const SAVED_RESET_MS = 1700;

/* scale manual limits down proportionally when the global cap drops below their sum */
function normalizeLimits(globalLimit: number, channels: Channel[]): Channel[] {
  DEBUG && console.log('Normalizing limits', { globalLimit, channels });

  const manual = channels.filter((c) => c.mode === 'manual' && c.enabled);
  const sum = manual.reduce((s, c) => s + c.manualLimit, 0);
  if (sum <= globalLimit || sum === 0) return channels;

  const ratio = globalLimit / sum;

  return channels.map((c) =>
    c.mode === 'manual' && c.enabled ? { ...c, manualLimit: Math.floor((c.manualLimit * ratio) / 5) * 5 } : c,
  );
}

const AdBudgetLimit = ({ onCloseBudgetLimits }: AdBudgetLimitProps) => {
  const { t } = useTranslation();
  // Reactive reveal flag: drives the re-derive effect below so gated channels unlock live
  // (and the allocator starts funding them) without a reload. See `useAcquisitionChannelGate`.
  const { revealed } = useAcquisitionChannelGate();
  /* ---------- state ---------- */
  const [globalLimit, setGlobalLimitRaw] = React.useState(240);
  const [channels, setChannels] = React.useState<Channel[]>(makeInitialChannels);
  const [activeTab, setActiveTab] = React.useState<'limits' | 'topup'>('topup');
  const [isSaved, setIsSaved] = React.useState(false);
  const [tuAmount, setTuAmount] = React.useState<number>(100);
  // Amount fields show the raw number while focused (native caret) and the grouped value
  // when blurred ("1,200"), matching the wallet payout field. See utils/money.ts.
  const [tuFocused, setTuFocused] = React.useState(false);
  // Raw text being typed in the top-up field (decoupled from the numeric `tuAmount` so an
  // in-progress "100." / partial decimal survives Number round-trips).
  const [tuDraft, setTuDraft] = React.useState('');
  const [globalLimitFocused, setGlobalLimitFocused] = React.useState(false);
  // Raw text the user is editing in the global-limit field (decoupled from the numeric
  // `globalLimit` so an in-progress "3." / partial decimal isn't eaten by Number round-trips).
  const [globalLimitDraft, setGlobalLimitDraft] = React.useState('');
  const [isGlobalLumioSelected, setIsGlobalLumioSelected] = React.useState(false);
  /** Shows the top-up confirmation step instead of the form. */
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  // The main-wallet deposit (Stripe) opened from the top-up tab when the wallet can't
  // fund the requested amount: the owner adds funds to MAIN, then tops up the ad budget.
  const [depositOpen, setDepositOpen] = React.useState(false);
  /** A charge has cleared but the balance it must be measured against hasn't been read yet. */
  const [awaitingBaseline, setAwaitingBaseline] = React.useState(false);
  /** How the last top-up ended, so the tab can say so instead of just closing the dialog. */
  const [topupOutcome, setTopupOutcome] = React.useState<{ ok: boolean; amount: number } | null>(null);

  /* ---------- acquisition-budget API (GET on open · POST on save) ---------- */

  const dispatch = useAppDispatch();
  // Brand whose budget this modal reads/writes. Use the lagged `displayBrand` (what the
  // surrounding page content shows), matching the Acquisition page's own source so the
  // card and modal never read different brands during a brand-switch fade. Keyed per
  // casino instance in the slice.
  const casinoInstanceId = useAppSelector((s) => s.casinoInstance.displayBrand?.id);
  // Tenant id for the Ad Service (per-channel ad-spend + CPA), matching the acquisition page.
  const tenantId = useAppSelector((s) => s.casinoInstance.displayBrand?.tenant_id);

  // Live per-channel figures (ad-spend today + blended CPA), self-sourced from the store so
  // the modal works identically from the acquisition card and the channel-detail page (both
  // mount it without passing data). Shared with the acquisition cards via the same hook.
  const { figures } = useAcquisitionChannelFigures(tenantId, casinoInstanceId);
  // Real figure for a channel: only enabled (live) channels; locked channels keep their
  // (hidden, frosted) scaffold numbers.
  const channelFigure = React.useCallback(
    (id: string): ChannelFigure | undefined => {
      const aft = acquisitionAftFor(id);
      return aft && isAcquisitionChannelEnabled(id) ? figures[aft] : undefined;
    },
    [figures],
  );
  // Allocation runs on the real CPA where available (better performers funded harder), with a
  // safe fallback to the scaffold CPA so the split never divides by a missing figure.
  const allocation = React.useMemo(
    () => computeAllocation(globalLimit, withRealCpa(channels, figures)),
    [globalLimit, channels, figures],
  );
  // Display name of the brand this budget belongs to, for the confirmation copy.
  // Flattened from `casino_data.details.brand` (same source the brand switcher uses);
  // undefined while a brand is still mid-setup or before the GET lands.
  const brandName = useAppSelector((s) => s.casinoInstance.displayBrand?.casino_data?.details?.brand);
  const budget = useAppSelector((s) => selectAcquisitionBudget(s, casinoInstanceId));
  const saving = useAppSelector((s) => s.acquisitionBudget.saving);

  // Main wallet is the source of funds for a top-up (the backend performs the actual
  // MAIN to acquisition transfer when we POST the new budget). We source the MAIN wallet in the
  // SELECTED CASINO's currency (falling back to the primary MAIN wallet in its own currency), so
  // the figure we SHOW and gate against reads in the same currency as the ad budget being topped
  // up. Clamped at 0 — a top-up already DEBITs MAIN, so `main` is net of committed ad budgets;
  // subtracting acquisition again would double-count it. `fetchOwnerWallets` is also dispatched by
  // the top bar.
  const ownerWallet = useAppSelector((s) => s.ownerWallet ?? null);
  const casinoCurrency = useAppSelector(selectSelectedCasinoCurrency);
  const walletView = resolveMainWalletForCurrency(ownerWallet, casinoCurrency);
  const walletLoaded = ownerWallet?.loaded ?? false;
  // `fetchOwnerWallets.rejected` still sets `loaded: true` while leaving the balance at its
  // initial 0, so without this flag a FAILED wallet fetch is indistinguishable from a genuinely
  // empty wallet. The funding guard below needs to tell those apart.
  const walletError = Boolean(ownerWallet?.error);
  const mainWalletBalance = Math.max(0, walletView.balance ?? 0);
  /* A Stripe deposit is credited server-side, so the balance does not move the instant the charge
   *  confirms. Watch it until it does rather than refetching once and hoping: putting money in and
   *  seeing nothing change is exactly where an owner starts to worry. */
  const {
    syncing: depositSyncing,
    credited: depositCredited,
    stalled: depositStalled,
    start: startDepositSync,
    retry: retryDepositSync,
    reset: resetDepositSync,
  } = useDepositSync(walletView.balance);
  /* `/wallet/list` drops `loaded` while a read is in flight, which nulls the resolved balance and
   *  would blank the figure to $0.00 on every poll tick. Latch the last REAL reading (it can't be
   *  derived from the current state, which is precisely "we don't know right now") and show that
   *  while a deposit settles. */
  const [lastKnownBalance, setLastKnownBalance] = React.useState(0);
  React.useEffect(() => {
    if (walletLoaded && walletView.balance !== null) setLastKnownBalance(Math.max(0, walletView.balance));
  }, [walletLoaded, walletView.balance]);
  const displayedMainBalance = walletLoaded ? mainWalletBalance : lastKnownBalance;
  // The MAIN balance renders in ITS currency: the casino currency when a matching MAIN wallet
  // exists, else the primary MAIN wallet's own currency.
  const walletMoney = (n: number): string =>
    `${currencySymbol(walletView.currency)}${formatAmountWithCode(n, walletView.currency)}`;

  // The acquisition/marketing wallet balance ("Available Ads Budget" / page "Remaining
  // Budget") is the API's `acquisition_budget`, the single source of truth, read live
  // from the slice (no local mock). `undefined` until the GET resolves.
  const acquisitionBudgetValue =
    typeof budget?.acquisition_budget === 'number' ? budget.acquisition_budget : undefined;
  // The GET has completed for this brand once the slice holds a record for it (the
  // reducer writes one on fulfilment, even when the brand has no budget yet). Gates
  // writes so we never POST before we know the current `acquisition_budget`.
  const budgetReady = budget !== undefined;

  // Currently available ads budget — the anchor for every suggested figure below.
  // 0 until the GET resolves, which keeps the suggestions on their fixed fallbacks.
  const availableBudget = acquisitionBudgetValue ?? 0;

  // Top-up preset chips scale with the available budget (lower budget → lower
  // suggestions), bracketing it at ½×, 1×, 2×. De-duped so a tiny budget can't
  // produce two identical chips; fixed fallback before the budget GET lands.
  const topupPresets = React.useMemo<number[]>(
    () =>
      availableBudget > 0
        ? Array.from(new Set([0.5, 1, 2].map((m) => roundToNice(availableBudget * m))))
        : TOPUP_PRESETS_FALLBACK,
    [availableBudget],
  );

  // Lumio's suggested top-up aims for ~5 days of runway at the current daily limit
  // (the gap between 5 days of spend and what's left), but is held within ½×–2× of
  // the available budget so it never reads as wildly off-course. Falls back to the
  // raw 5-day target before any budget is known.
  const lumioSuggestedTopup = React.useMemo<number>(() => {
    let suggestion: number;
    if (availableBudget <= 0) {
      suggestion = roundToNice(globalLimit * 5) || TOPUP_PRESETS_FALLBACK[2];
    } else {
      const fiveDayGap = globalLimit * 5 - availableBudget;
      const balanced = Math.min(Math.max(fiveDayGap, availableBudget * 0.5), availableBudget * 2);
      suggestion = roundToNice(balanced);
    }
    // Keep Lumio's pick visually distinct from the standard chips — if it lands on
    // one of them, nudge up a clean step until it no longer coincides.
    const standard = new Set(topupPresets);
    while (standard.has(suggestion)) suggestion += niceStep(suggestion);
    return suggestion;
  }, [availableBudget, globalLimit, topupPresets]);

  // Daily-limit preset chips: caps that stretch the available budget across 6 / 5 / 4
  // days (ascending $, rounded up to clean $5 steps); the Lumio chip targets a longer
  // 7-day stretch (see `lumioDailyLimit`). De-duped for tiny budgets.
  const dailyLimitPresets = React.useMemo<{ value: number; currency: string }[]>(
    () =>
      (availableBudget > 0
        ? Array.from(new Set([6, 5, 4].map((days) => roundUpToFive(availableBudget / days))))
        : [25, 50, 100]
      ).map((value) => ({ value, currency: '$' })),
    [availableBudget],
  );

  // Lumio's daily-limit pick: split the budget over ~7 days (the longest stretch, so
  // the lowest cap), kept distinct from the 6/5/4-day chips by nudging DOWN on a
  // collision (an even longer stretch). Mirrors the top-up tab's distinct-value chip.
  const lumioDailyLimit = React.useMemo<number>(() => {
    const lowestPreset = dailyLimitPresets[0]?.value ?? 25;
    let limit = availableBudget > 0 ? roundUpToFive(availableBudget / 7) : Math.max(5, lowestPreset - 5);
    const standard = new Set(dailyLimitPresets.map((p) => p.value));
    while (limit > 5 && standard.has(limit)) limit -= 5;
    return limit;
  }, [availableBudget, dailyLimitPresets]);

  // GET the brand's budget + wallets when the modal mounts (it only mounts while open)
  // and whenever the active brand changes underneath it. Close any open confirmation on a
  // brand switch so a pending top-up can't be applied to the newly-selected brand.
  React.useEffect(() => {
    if (!casinoInstanceId) return;
    setConfirmOpen(false);
    /* And the card checkout with it. The brand selector sits in the top bar, outside the
     *  backdrop the desktop overlay pins to the content card, so it stays clickable while the
     *  deposit is open. The PaymentIntent was minted in the PREVIOUS brand's wallet currency
     *  and every figure on that form re-labels itself in the new one, so leaving it up would
     *  show the owner one price and charge them the other. */
    setDepositOpen(false);
    // A post-deposit watch is keyed to the balance we saw for the previous brand, so drop it
    // rather than let it report a "credit" that is really just the switch.
    resetDepositSync();
    setAwaitingBaseline(false);
    // Whatever the last top-up did, it was to the previous brand's budget.
    setTopupOutcome(null);
    dispatch(fetchAcquisitionBudget({ casinoInstanceId }));
    dispatch(fetchOwnerWallets());
  }, [dispatch, casinoInstanceId, resetDepositSync]);

  // Re-derive each channel's lock state whenever the runtime reveal flag flips, so gated
  // channels unlock live (the allocation engine reads `enabled`, so the split + bar follow).
  // Returns `prev` unchanged when nothing moved, so it costs no render on mount/no-op flips.
  React.useEffect(() => {
    setChannels((prev) => {
      let changed = false;
      const next = prev.map((c) => {
        const enabled = isAcquisitionChannelEnabled(c.id);
        if (enabled === c.enabled) return c;
        changed = true;
        return { ...c, enabled };
      });
      return changed ? next : prev;
    });
  }, [revealed]);

  // Hydrate the daily-limit controls from the loaded budget ONCE per brand, so a late
  // GET (or a re-render after a save) never clobbers edits the user is mid-way through.
  // The store budget only changes on fetch/save fulfilment, not on local edits. The
  // "Available Ads Budget" figure is NOT hydrated here; it reads live from the slice.
  const hydratedFor = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!casinoInstanceId || !budget) return;
    if (hydratedFor.current === casinoInstanceId) return;
    hydratedFor.current = casinoInstanceId;

    if (typeof budget.daily_acquisition_budget === 'number') {
      const daily = budget.daily_acquisition_budget;
      setGlobalLimitRaw(daily);
      setChannels((prev) => normalizeLimits(daily, prev));
    }
    const details = budget.budget_details;
    if (details) {
      setChannels((prev) =>
        prev.map((c) => {
          const next = { ...c };
          if (typeof details[c.id] === 'number') next.manualLimit = details[c.id];
          // Restore the saved mode from `<id>_managed` (true = Lumio, false = manual);
          // fall back to the channel's default when the key is absent (older records).
          const managed = details[`${c.id}_managed`];
          if (typeof managed === 'boolean') next.mode = managed ? 'lumio' : 'manual';
          // `enabled` is a release feature flag, never persisted per-brand: always
          // re-derive it from the single source of truth so saved data can't carry a
          // stale lock state across a flag change.
          next.enabled = isAcquisitionChannelEnabled(c.id);
          return next;
        }),
      );
    }
  }, [casinoInstanceId, budget]);

  /** Per-channel daily split for `budget_details`, keyed by the `aft` channel literals
   *  ({@link BUDGET_CHANNEL_IDS}). For each literal we persist two keys:
   *   - `<id>`: the effective allocated amount per channel (number).
   *   - `<id>_managed`: whether Lumio manages it (true = automatic/Lumio, false =
   *     manual). The API's `budget_details` is a free-form bag with no dedicated mode
   *     field, so this lets the per-channel mode round-trip across save/load. */
  const buildBudgetDetails = React.useCallback(
    (): Record<string, number | boolean> =>
      BUDGET_CHANNEL_IDS.reduce<Record<string, number | boolean>>((acc, id) => {
        acc[id] = allocation.alloc[id] ?? 0;
        acc[`${id}_managed`] = channels.find((c) => c.id === id)?.mode === 'lumio';
        return acc;
      }, {}),
    [allocation, channels],
  );

  /* ---------- actions (wired straight to the DOM events the template nodes declared) ---------- */

  const setGlobalLimit = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Accept a money amount (digits + up to 2 decimals). We keep the raw text as the draft so
    // a half-typed "3." survives, and parse it to the numeric limit the allocation runs on.
    // (Typed limits may be fractional; the +/- steppers, by contrast, snap to clean $5 steps.)
    const cleaned = cleanAmountInput(e.target.value ?? '');
    setGlobalLimitDraft(cleaned);
    const value = cleaned === '' ? 0 : parseFloat(cleaned) || 0;

    DEBUG && console.log('Setting global limit', { value });

    setGlobalLimitRaw(value);
    setChannels((prev) => normalizeLimits(value, prev));
  }, []);

  /* node `cGqLSeJrVZ` onClick → setPresetGlobalLimit (reads data-value off the chip button) */
  const setPresetGlobalLimit = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const value = parseInt(e.currentTarget.dataset.value ?? '0', 10);

    DEBUG && console.log('Setting preset global limit', { value });

    setGlobalLimitRaw(value);
    setIsGlobalLumioSelected(false);
    setChannels((prev) => normalizeLimits(value, prev));
  }, []);

  const setGlobalLimitUsingLumio = React.useCallback(() => {
    // Apply Lumio's ~7-day daily cap (see `lumioDailyLimit`).
    setGlobalLimitRaw(lumioDailyLimit);
    setIsGlobalLumioSelected(true);
    setChannels((prev) => normalizeLimits(lumioDailyLimit, prev));
  }, [lumioDailyLimit]);

  /* node `_hoasJGK-i` onClick → toggleChannelMode (reads data-channelid off the switch) */
  const toggleChannelMode = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const id = e.currentTarget.dataset.channelid ?? '';

      setChannels((prev) => {
        // Gated channels can't change mode (their controls sit under a lock veil).
        const target = prev.find((c) => c.id === id);
        if (!target || !target.enabled) return prev;

        const current = computeAllocation(globalLimit, prev);

        DEBUG && console.log('toggleChannelMode', { current, id });

        return prev.map((c) => {
          if (c.id !== id) return c;
          if (c.mode === 'manual') {
            return { ...c, mode: 'lumio' as ChannelMode };
          }
          const otherManualSum = prev.filter((x) => x.mode === 'manual').reduce((s, x) => s + x.manualLimit, 0);
          const prev_alloc = current.alloc[id] ?? c.manualLimit;
          const newLimit = Math.max(0, Math.min(globalLimit - otherManualSum, prev_alloc));
          return { ...c, mode: 'manual' as ChannelMode, manualLimit: newLimit };
        });
      });
    },
    [globalLimit],
  );

  const getChannelHeadroom = React.useCallback(
    (id: string): number => {
      const otherManualSum = channels
        .filter((c) => c.mode === 'manual' && c.id !== id)
        .reduce((s, c) => s + c.manualLimit, 0);

      DEBUG && console.log('Calculating headroom for channel', { id, globalLimit, otherManualSum });
      return Math.max(0, globalLimit - otherManualSum);
    },
    [globalLimit, channels],
  );

  /* node `IJevbat-Aa` onChange → setChannelLimit (reads data-channelid + value off the range input) */
  const setChannelLimit = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const id = e.currentTarget.dataset.channelid ?? '';
      const value = parseInt(e.target.value ?? '0', 10);

      console.log('Setting channel limit', { id, value, ev: e.currentTarget });

      setChannels((prev) => {
        const ch = prev.find((c) => c.id === id);
        // Gated channels can't move their slider (it sits under a lock veil).
        if (!ch || !ch.enabled) return prev;

        const otherChannelsSum = prev.filter((c) => c.id !== id).reduce((s, c) => s + c.manualLimit, 0);
        const capped = Math.min(value, Math.max(0, globalLimit - otherChannelsSum));

        DEBUG && console.log('Setting channel limit', { id, value, capped, globalLimit, otherChannelsSum });

        return prev.map((c) => (c.id === id ? { ...c, manualLimit: capped } : c));
      });
    },
    [globalLimit],
  );

  /* node `qQd2VENEWm` text ← lumioReason; falls back to a computed reason by CPA rank */
  const getLumioReason = React.useCallback(
    (id: string): string => {
      const sorted = [...channels].sort((a, b) => a.cpa - b.cpa);
      const rank = sorted.findIndex((c) => c.id === id);
      if (rank === 0) return t('Best CPA on the account, scaled up');
      if (rank === channels.length - 1) return t('Weakest CPA, Lumio scaled it back');

      DEBUG && console.log('Calculating Lumio reason', { id, rank, sorted });

      return t('Steady performer, Lumio holding the line');
    },
    [channels, t],
  );

  /* node `6039:32478` onChange → free-type top-up amount (money: digits + up to 2 decimals) */
  const setTopupAmount = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Keep the raw text as the draft (so a half-typed "100." survives) and parse it to the
    // numeric amount used by the presets / confirm / POST.
    const cleaned = cleanAmountInput(e.target.value ?? '');
    setTuDraft(cleaned);
    setTuAmount(cleaned === '' ? 0 : parseFloat(cleaned) || 0);
  }, []);
  const onTopupFocus = React.useCallback(() => {
    // Seed the editable draft from the current amount (chips/Lumio set the number directly).
    setTuDraft(tuAmount > 0 ? String(tuAmount) : '');
    setTuFocused(true);
  }, [tuAmount]);
  const onTopupBlur = React.useCallback(() => setTuFocused(false), []);
  const onGlobalLimitFocus = React.useCallback(() => {
    // Seed the editable draft from the current numeric limit (steppers/presets/Lumio update
    // the number directly, so re-sync each time the field is entered).
    setGlobalLimitDraft(String(globalLimit));
    setGlobalLimitFocused(true);
  }, [globalLimit]);
  const onGlobalLimitBlur = React.useCallback(() => setGlobalLimitFocused(false), []);

  /* "Save Daily Limits" → POST the per-day cap + per-channel split, preserving the
   *  current acquisition wallet budget (only a top-up changes that). Gated on
   *  `budgetReady` so we never overwrite `acquisition_budget` before the GET resolves. */
  const save = React.useCallback(() => {
    if (!casinoInstanceId || !budgetReady) {
      DEBUG && console.warn('[AdBudgetLimit] save skipped (no active casino instance / budget not loaded)');
      return;
    }

    setIsSaved(true);
    setTimeout(() => setIsSaved(false), SAVED_RESET_MS);

    dispatch(
      saveAcquisitionBudget({
        casinoInstanceId,
        acquisitionBudget: acquisitionBudgetValue ?? 0,
        dailyAcquisitionBudget: globalLimit,
        budgetDetails: buildBudgetDetails(),
      }),
    );
  }, [dispatch, casinoInstanceId, budgetReady, acquisitionBudgetValue, globalLimit, buildBudgetDetails]);

  /* Known to be a different currency, which is NOT the same question as `walletView.matched`.
   *  The resolver reports `matched: false` both for a genuine cross-currency wallet and for a
   *  brand that carries no currency at all (no campaign yet, e.g. mid-deploy), where it simply
   *  falls back to the wallet's own. Only the first is a reason to distrust the comparison;
   *  treating the second the same way leaves those owners with a disabled button and no action
   *  that could ever change it. An unknown currency is not evidence of a mismatch. */
  const budgetCurrency = (casinoCurrency ?? '').trim().toUpperCase();
  const heldCurrency = (walletView.currency ?? '').trim().toUpperCase();
  const currencyMismatch = Boolean(budgetCurrency) && Boolean(heldCurrency) && budgetCurrency !== heldCurrency;
  const currenciesComparable = !currencyMismatch;

  /* The main wallet funds the top-up, so it has to actually cover the amount. This guard is
   *  the UI's own: a balance we can't confirm (empty, still loading, unreadable, or held in
   *  a different currency) never counts as enough, so the flow can't promise a top-up it has
   *  no reason to believe in. It is not an authorization boundary, and nothing here should be
   *  read as one. Pure logic + its regression tests live in `./topup-funding`. */
  const funding = assessTopupFunding({
    amount: tuAmount,
    balance: walletView.balance,
    walletLoaded,
    walletError,
    /* Refuse the comparison only when the two currencies are KNOWN to differ. Comparing a
     *  balance held in one currency against an amount priced in another would gate real money
     *  on an exchange rate nobody applied. But `walletView.matched` is false in two unrelated
     *  situations, and only one of them is that: a brand with no campaign yet has no currency
     *  at all, and the resolver then falls back to the wallet's own by design (its docstring
     *  says so). Reading that as a mismatch would dead-end every one of those owners, with no
     *  action on screen able to clear it. */
    comparable: currenciesComparable,
  });
  /* Not while a save is in flight. `confirmTopup` POSTs an ABSOLUTE budget computed from the
   *  figure it last read, so a second confirm before the first lands (or just after, on a tab
   *  that looks unchanged because nothing on it reports success) adds the amount twice and the
   *  main wallet is debited twice for one intended top-up. */
  const canConfirmTopup = Boolean(casinoInstanceId) && budgetReady && funding.canFund && !saving;
  /* Every post-deposit read drops `loaded` while it is in flight, which flips the funding status
   *  between `loading` and `short` about once a second. Hold ONE stable "settling" state for the
   *  whole watch so the CTA and the warning don't strobe while the owner waits on their money. */
  const walletSettling = funding.status === 'loading' || depositSyncing || awaitingBaseline;
  /* The wallet can't pay for this top-up, so the tab swaps its CTA for the main-wallet
   *  deposit instead of offering a button that can only fail.
   *
   *  ONLY for a balance we have actually read and found short. A failed read is not a reason
   *  to charge a card (the owner may hold plenty; the honest next step is to read it again),
   *  and neither is a deposit whose credit is still in flight: offering "Add Funds" under a
   *  note saying the payment went through is how an owner pays twice for the same thing. */
  const needsMainWalletFunds = !walletSettling && !depositStalled && funding.status === 'short';

  /* Top-up tab "Top Up …" → ask for confirmation first (no silent money movement). */
  const openTopupConfirm = React.useCallback(() => {
    if (!canConfirmTopup) return;
    setConfirmOpen(true);
  }, [canConfirmTopup]);

  /* "Add Funds to Main Wallet" → the same Stripe deposit the wallet page uses. Stripe confirms
   *  the charge before the backend credits the wallet, so on success we WATCH the balance until
   *  the money shows up (see `useDepositSync`) rather than refetching once. The funding guard
   *  above reads the same balance, so the top-up CTA comes back by itself the moment it lands,
   *  without the owner leaving the modal. */
  const openDeposit = React.useCallback(() => setDepositOpen(true), []);
  const closeDeposit = React.useCallback(() => setDepositOpen(false), []);
  const onDeposited = React.useCallback(() => {
    /* Baseline is the balance as it reads right now, BEFORE the credit lands, and it has to
     *  be a figure we actually READ. `/wallet/list` failing leaves the balance at 0 while
     *  reporting itself loaded, and a 0 baseline makes the first successful read look like
     *  the deposit: an owner who put in 50 would be told their whole 2,000 had just arrived.
     *  With no trustworthy baseline, read the wallet again and start the watch from whatever
     *  comes back, holding the tab in its settling state until it does. Simply giving up here
     *  would leave the owner on a "your wallet is short" strip seconds after paying, with the
     *  deposit CTA offering to take their money a second time. */
    if (!walletLoaded || walletError || walletView.balance === null) {
      setAwaitingBaseline(true);
      dispatch(fetchOwnerWallets());
      return;
    }
    startDepositSync(displayedMainBalance);
  }, [startDepositSync, displayedMainBalance, walletLoaded, walletError, walletView.balance, dispatch]);
  React.useEffect(() => {
    // The re-read settled. A real figure starts the watch; a second failure hands over to the
    // unknown strip (which offers another read and never a card charge), so this can't hang.
    if (!awaitingBaseline || !walletLoaded) return;
    setAwaitingBaseline(false);
    if (walletError || walletView.balance === null) return;
    startDepositSync(Math.max(0, walletView.balance));
  }, [awaitingBaseline, walletLoaded, walletError, walletView.balance, startDepositSync]);
  /* Closing is the Overlay's own business, and Escape reaches only the dialog on top, so
   *  there is nothing to veto from out here: a caller that refuses a close the sheet has
   *  already begun leaves it mounted, invisible and swallowing every click. */
  const closeBudgetLimits = React.useCallback(() => onCloseBudgetLimits?.(), [onCloseBudgetLimits]);
  /* Read the wallet again after a failed read, the one action that can actually clear an
   *  `unknown` balance. */
  const rereadWallet = React.useCallback(() => {
    dispatch(fetchOwnerWallets());
  }, [dispatch]);

  const cancelTopup = React.useCallback(() => setConfirmOpen(false), []);

  /* The keyboard's way through the confirmation, laid over the sheet as a dialog of its own: the focus moves in as it
   *  opens, Tab goes round its buttons (the sheet stands down while it lies over it), and the focus goes back to Top Up
   *  as it closes. */
  const confirmRef = React.useRef<HTMLDivElement>(null);
  useDialogFocus(confirmRef, confirmOpen);
  React.useEffect(() => {
    if (!confirmOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Tab') keepTabIn(event, confirmRef.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmOpen]);

  // Close only when the backdrop itself is clicked (not a click bubbling from the card).
  const onConfirmBackdrop = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) setConfirmOpen(false);
  }, []);

  /* Confirmed top-up → POST the increased acquisition wallet budget; the BACKEND moves
   *  the funds from the owner's main wallet (we don't call a wallet-transfer API). A
   *  top-up must NOT commit unsaved Daily-Limits edits, so we send the backend's
   *  last-SAVED daily cap + per-channel split (from the slice), not the local form state
   *  (which the user may have edited on the other tab without saving). Refresh wallets
   *  afterwards so the main-wallet balance reflects the debit. */
  const confirmTopup = React.useCallback(() => {
    if (!canConfirmTopup || !casinoInstanceId) return;

    setConfirmOpen(false);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), SAVED_RESET_MS);

    /* Say what happened, either way. `isSaved` is rendered only by the Daily Limits footer, so
     *  from this tab both outcomes looked identical: the dialog closed and nothing else moved.
     *  A `.then()` on the thunk doesn't help, it runs for a rejection too, and the slice's
     *  `saveError` is read nowhere. An owner who has just asked to move money must not be left
     *  guessing whether it moved. */
    const moved = tuAmount;
    setTopupOutcome(null);
    const nextBudget = (acquisitionBudgetValue ?? 0) + tuAmount;
    void dispatch(
      saveAcquisitionBudget({
        casinoInstanceId,
        acquisitionBudget: nextBudget,
        dailyAcquisitionBudget:
          typeof budget?.daily_acquisition_budget === 'number' ? budget.daily_acquisition_budget : globalLimit,
        budgetDetails: budget?.budget_details ?? buildBudgetDetails(),
      }),
    )
      .unwrap()
      .then(() => setTopupOutcome({ ok: true, amount: moved }))
      .catch(() => setTopupOutcome({ ok: false, amount: moved }))
      // Re-read the wallet whichever way it went: the figure on screen has to match what the
      // server now holds, especially when we are about to tell the owner it didn't work.
      .finally(() => {
        void dispatch(fetchOwnerWallets());
      });
  }, [dispatch, canConfirmTopup, casinoInstanceId, acquisitionBudgetValue, tuAmount, budget, globalLimit, buildBudgetDetails]);

  /* node `pQ2rS3tU4v` onClick → increaseLimit. Doubles as the base, then snaps to a clean
   *  $5 increment (divisible by 5, $5 min, no decimals) via {@link roundUpToFive}. */
  const increaseLimit = React.useCallback(() => {
    const next = roundUpToFive(globalLimit * 2);
    setGlobalLimitRaw(next);
    setChannels((prev) => normalizeLimits(next, prev));
    setIsGlobalLumioSelected(false);
  }, [globalLimit]);

  /* node `iJ9kL0mN1o` onClick → decreaseLimit. Halves as the base, then snaps to a clean
   *  $5 increment (divisible by 5, $5 min, no decimals) via {@link roundUpToFive} — so it
   *  bottoms out at $5 and never produces fractional caps. */
  const decreaseLimit = React.useCallback(() => {
    const next = roundUpToFive(globalLimit / 2);
    setGlobalLimitRaw(next);
    setChannels((prev) => normalizeLimits(next, prev));
    setIsGlobalLumioSelected(false);
  }, [globalLimit]);

  /* enrich each channel with the per-channel slider geometry the template read (progress / maxRange / maxSliderLenght) */
  const calculateProgressBars = React.useCallback(() => {
    const manualLimitSum = channels.reduce((s, c) => s + (c.manualLimit ?? 0), 0);

    return channels.map((c) => {
      const totalNotAllocated = globalLimit - manualLimitSum;
      const maxRange = c.manualLimit + totalNotAllocated;

      let progress = (c.manualLimit / maxRange) * 100;
      if (progress > 100) progress = 100;
      if (progress < 0) progress = 0;

      return {
        ...c,
        manualLimitSum,
        progress,
        maxRange, // max slider range for this channel
        maxSliderLenght: (maxRange / globalLimit) * 100, // max slider length as percent of global limit
      };
    });
  }, [channels, globalLimit]);

  /* ---------- render helpers (the old per-node propFunctions, inlined) ---------- */

  const presetLimits = dailyLimitPresets; // budget-scaled caps for 6 / 5 / 4 days

  const channelRows = calculateProgressBars();

  // Amount-field display: raw while focused (native caret), grouped when blurred.
  const tuAmountDisplay = tuFocused ? tuDraft : tuAmount > 0 ? formatAmountDisplay(String(tuAmount)) : '';
  const globalLimitDisplay = globalLimitFocused ? globalLimitDraft : formatAmountDisplay(String(globalLimit));

  // The channel display names are a fixed UI vocabulary (defined in INITIAL_CHANNELS);
  // translate at the render site over the known set so the copy re-translates on a
  // live language switch. An unrecognised name renders as-is.
  const channelName = (name: string): string => {
    const labels: Record<string, string> = {
      'Web Networks': t('Web Networks'),
      'App Networks': t('App Networks'),
      'Crypto Sites': t('Crypto Sites'),
      'Short Videos': t('Short Videos'),
    };
    return labels[name] ?? name;
  };

  // nodes `jK3lM4nO5p` / `xY9zA0bC1d` / `qR6sT7uV8w` / `eF2gH3iJ4k` / `0msbprT-JK`: segment width as % of the global cap
  const segWidth = (value: number): number => {
    if (!globalLimit) return 0;
    let prg = ((value ?? 0) * 100) / globalLimit;
    if (prg >= 100) prg = 100;
    if (prg < 0) prg = 0;
    return prg;
  };

  // node `dZ6pB5lGNw`: slider thumb offset
  const thumbLeft = (progress: number, maxSliderLenght: number): string => {
    if (maxSliderLenght === 0) return `${progress}%`;
    if (progress < 3) return `${progress}%`;
    if (progress > 97) return `calc(${progress}% - 16px)`;
    return `calc(${progress}% - 8px)`;
  };

  // console.log('Rendering AdBudgetLimit', { globalLimit, channels, allocation });

  // Pinned footer for the Daily-Limits tab (the allocation status + Save) — handed
  // to the shared Overlay's footer slot. Wrapped in `.ad-budget-limits` so the
  // scoped footer styles reach it (the Overlay renders the footer outside the body),
  // and the CTA is the shared <Button>. The Top-Up tab keeps its own inline pay CTA.
  const limitsFooter = (
    <div className="ad-budget-limits">
      <div className="abl-limits-footer">
        <Button
          label={saving ? t('Saving…') : isSaved ? t('Saved') : t('Save Daily Limits')}
          variant="primary"
          fullWidth
          onClick={save}
          disabled={allocation.over > 0 || saving || !budgetReady}
        />
      </div>
    </div>
  );

  return (
    <>
      <Sheet
        title={t('Ads Budget & Daily Limits')}
        onClose={closeBudgetLimits}
        footer={activeTab === 'limits' ? limitsFooter : undefined}
        // The Daily-Limits tab (global cap + allocation + four channel cards) is
        // tall: let the sheet grow to the full available viewport height instead of
        // the 720px default, matching the activation + deposit modals, so it only
        // scrolls internally when it truly runs out of room.
        maxHeight="100%"
        // Smoothly grow/shrink the sheet when switching tabs (Top Up ↔ Daily
        // Limits) instead of snapping to the new height.
        animateResize
      >
        <div className="ad-budget-limits">
          {/* ===== sticky tabs ===== */}
          <div className="abl-tabs">
            <div className="abl-tab-row" role="tablist" aria-label={t('Ad budget views')}>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'topup'}
                className={`abl-tab${activeTab === 'topup' ? ' abl-tab-active' : ''}`}
                onClick={() => setActiveTab('topup')}
              >
                <Text text="Top Up Budget" />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'limits'}
                className={`abl-tab${activeTab === 'limits' ? ' abl-tab-active' : ''}`}
                onClick={() => setActiveTab('limits')}
              >
                <Text text="Daily Limits" />
              </button>
            </div>
          </div>

          {activeTab === 'topup' && (
            <div className="tu-root">
              {/* ===== balances: main wallet → current ad budget (node 6039:32444) =====
                  Two cards with an arrow showing where a top-up's funds flow FROM (the
                  owner's main wallet) and INTO (the acquisition / ad budget). */}
              <div className="tu-balances">
                <div className="tu-balance-card">
                  <span className="tu-balance-label"><Text text="Main Wallet Balance" /></span>
                  {/* The balance holds its last known figure while a deposit settles rather than
                      blanking to "–" on every read: a number that vanishes mid-payment reads as
                      money lost. The pill below carries the "we're on it" signal instead. */}
                  <span className="tu-balance-value">
                    {/* A failed read leaves the slice reporting `loaded` with the balance still at
                        its initial 0, so printing it would state "$0.00" as fact directly above a
                        strip saying we couldn't read it. We don't know the figure: say so. */}
                    {walletError && !depositSyncing
                      ? '–'
                      : walletLoaded || depositSyncing
                        ? walletMoney(displayedMainBalance)
                        : '–'}
                  </span>
                  {depositSyncing && (
                    <span className="tu-balance-sync" role="status">
                      <span className="tu-balance-sync-dot" aria-hidden="true" />
                      <Text tag="span" className="tu-balance-sync-text" text="Waiting for your deposit…" />
                    </span>
                  )}
                  {depositCredited !== null && (
                    <span className="tu-balance-credited" role="status">
                      <Text
                        tag="span"
                        className="tu-balance-credited-text"
                        text="{{amount}} added"
                        placeholders={{ amount: walletMoney(depositCredited) }}
                      />
                    </span>
                  )}
                </div>
                <span className="tu-balance-arrow" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4 12h15M13 6l6 6-6 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div className="tu-balance-card">
                  <span className="tu-balance-label">
                    {brandName ? (
                      <Text text="Current Ads Budget for {{brandName}}" placeholders={{ brandName }} />
                    ) : (
                      <Text text="Current Ads Budget" />
                    )}
                  </span>
                  <span className="tu-balance-value">
                    {acquisitionBudgetValue === undefined ? '–' : formatMoney(acquisitionBudgetValue)}
                  </span>
                </div>
              </div>

              {/* ===== top up budget group (node 6039:32472) ===== */}
              <div className="tu-budget-group">
                {/* ===== buy ad spend credit (node 6039:33373) ===== */}
                <div className="tu-section">
                  <span className="tu-section-label"><Text text="One Time Top Up Amount" /></span>

                  {/* amount input (node 6039:32476) */}
                  <div className="tu-amount-input">
                    <span className="tu-amount-currency">$</span>
                    <input
                      className="tu-amount-value"
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={tuAmountDisplay}
                      onChange={setTopupAmount}
                      onFocus={onTopupFocus}
                      onBlur={onTopupBlur}
                    />
                    <span className="tu-amount-hint"><Text text="Amount" /></span>
                  </div>

                  {/* preset chips (node 6039:32481) */}
                  <div className="tu-chips">
                    {topupPresets.map((value) => (
                      <div
                        key={value}
                        className={`tu-chip${tuAmount === value ? ' selected' : ''}`}
                        onClick={() => setTuAmount(value)}
                      >
                        <span className="tu-chip-text">{formatMoney(value)}</span>
                      </div>
                    ))}
                    <div
                      className={`tu-chip tu-chip-lumio${tuAmount === lumioSuggestedTopup ? ' selected' : ''}`}
                      onClick={() => setTuAmount(lumioSuggestedTopup)}
                    >
                      <LumioSparkle className="tu-chip-sparkle" />
                      <span className="tu-chip-text">{formatMoney(lumioSuggestedTopup)}</span>
                    </div>
                  </div>
                </div>

                {/* ===== top up + funding note (node 6039:33375) =====
                    The main wallet funds this transfer, so when it can't cover the amount the
                    only useful next step is adding funds to it: the CTA becomes that deposit
                    (card checkout) rather than a top-up button the wallet can't honour. */}
                <div className="tu-pay-section">
                  {!walletSettling && funding.status === 'short' && (
                    <div className="tu-funding-note" role="status">
                      <span className="tu-funding-note-icon" aria-hidden="true">!</span>
                      <span className="tu-funding-note-text">
                        <Text
                          text="Your main wallet holds {{balance}}. Add {{shortfall}} to cover this top-up."
                          placeholders={{
                            balance: walletMoney(mainWalletBalance),
                            shortfall: walletMoney(funding.shortfall),
                          }}
                        />
                      </span>
                    </div>
                  )}
                  {/* An unreadable balance is not a reason to charge a card: the owner may
                      hold plenty and simply not have been told. The action that fits is
                      reading it again. */}
                  {!walletSettling && funding.status === 'unknown' && !currencyMismatch && (
                    <div className="tu-funding-note" role="status">
                      <span className="tu-funding-note-icon" aria-hidden="true">!</span>
                      <span className="tu-funding-note-text">
                        <Text text="We couldn't read your main wallet balance, so this top-up can't be confirmed yet." />
                      </span>
                      <button type="button" className="tu-funding-note-action" onClick={rereadWallet}>
                        <Text text="Check again" />
                      </button>
                    </div>
                  )}
                  {/* A wallet in another currency was read perfectly well, so saying we couldn't
                      read it would be a lie, and re-reading it would change nothing. Name the
                      actual obstacle instead. */}
                  {!walletSettling && funding.status === 'unknown' && currencyMismatch && (
                    <div className="tu-funding-note" role="status">
                      <span className="tu-funding-note-icon" aria-hidden="true">!</span>
                      <span className="tu-funding-note-text">
                        <Text
                          text="Your main wallet is held in {{held}}, and this top-up is priced in {{priced}}, so it can't fund this one."
                          placeholders={{ held: heldCurrency, priced: budgetCurrency }}
                        />
                      </span>
                    </div>
                  )}
                  {/* The deposit ran but the credit hasn't surfaced within the watch window.
                      Say so plainly and hand back an action instead of leaving a dead spinner. */}
                  {/* How the last top-up ended. Both outcomes are stated: silence after a
                      money instruction is read as success, and on failure it is the difference
                      between trying again and assuming a debit already happened. */}
                  {topupOutcome?.ok && (
                    <div className="tu-funding-note tu-funding-note-ok" role="status">
                      <span className="tu-funding-note-icon" aria-hidden="true">✓</span>
                      <span className="tu-funding-note-text">
                        <Text
                          text="{{amount}} moved to your ads budget."
                          placeholders={{ amount: formatMoney(topupOutcome.amount) }}
                        />
                      </span>
                    </div>
                  )}
                  {topupOutcome && !topupOutcome.ok && (
                    <div className="tu-funding-note" role="alert">
                      <span className="tu-funding-note-icon" aria-hidden="true">!</span>
                      <span className="tu-funding-note-text">
                        <Text text="That top-up didn't go through. Check your ads budget above before trying again." />
                      </span>
                    </div>
                  )}
                  {/* Dropped as soon as the balance covers the amount: a credit that lands
                      after the watch gave up must not leave the owner reading that their
                      money never arrived. */}
                  {depositStalled && funding.status !== 'covered' && (
                    <div className="tu-funding-note tu-funding-note-pending" role="status">
                      <span className="tu-funding-note-icon" aria-hidden="true">!</span>
                      <span className="tu-funding-note-text">
                        <Text text="Your payment went through, but the new balance hasn't appeared yet. It can take a few minutes." />
                      </span>
                      <button type="button" className="tu-funding-note-action" onClick={retryDepositSync}>
                        <Text text="Check again" />
                      </button>
                    </div>
                  )}
                  {needsMainWalletFunds ? (
                    <Button
                      label={t('Add Funds to Main Wallet')}
                      variant="primary"
                      fullWidth
                      onClick={openDeposit}
                    />
                  ) : (
                    <Button
                      label={
                        walletSettling
                          ? t('Checking your wallet balance…')
                          : t('Top Up ${{tuAmount}}', { tuAmount: formatAmountDisplay(String(tuAmount)) })
                      }
                      variant="primary"
                      fullWidth
                      onClick={openTopupConfirm}
                      disabled={!canConfirmTopup || walletSettling}
                    />
                  )}
                  <div className="tu-security-note">
                    <span className="tu-security-lock">🔒</span>
                    <span className="tu-security-text"><Text text="Funds move from your main wallet" /></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'limits' && (
            <div className="abl-limits-root-tab">
              {/* ===== global daily spend limit (node oP7qR8sT9u) ===== */}
              <div className="abl-section abl-spend-limit">
                <span className="abl-section-title"><Text text="Global Daily Spend Limit" /></span>
                <span className="abl-section-sub"><Text text="Total spend cap across all channels, per day" /></span>

                <div className="abl-input-row">
                  <div className="abl-amount-field">
                    <span className="abl-amount-value">$&nbsp;</span>
                    <input
                      id="global-limit"
                      className="abl-amount-value"
                      type="text"
                      inputMode="decimal"
                      value={globalLimitDisplay}
                      onChange={setGlobalLimit}
                      onFocus={onGlobalLimitFocus}
                      onBlur={onGlobalLimitBlur}
                    />
                    <span className="abl-amount-unit"><Text text="/day" /></span>
                  </div>
                  <div className="abl-stepper-btn" onClick={decreaseLimit}>
                    <span className="abl-stepper-text">–</span>
                  </div>
                  <div className="abl-stepper-btn" onClick={increaseLimit}>
                    <span className="abl-stepper-text">+</span>
                  </div>
                </div>

                <div className="abl-chips-row-wrapper">
                  {/* preset chips (node H0YgednuVq — ModuleList over presetLimits) */}
                  <div className="abl-chips-row">
                    {presetLimits.map(({ value, currency }) => (
                      <div
                        key={value}
                        className={`abl-chip-wrp${globalLimit === value ? ' selected' : ''}`}
                        data-value={value}
                        onClick={setPresetGlobalLimit}
                      >
                        <span className="abl-chip-text" data-value={value}>{`${currency}${formatAmountDisplay(String(value))}`}</span>
                      </div>
                    ))}
                  </div>

                  {/* lumio suggested chip — last in the row, showing its 7-day daily-limit
                      value (icon + value), mirroring the top-up tab's Lumio chip (node tU5vW6xY7z) */}
                  <div
                    className={`abl-chip-wrp abl-chip-lumio ${isGlobalLumioSelected ? 'selected' : ''}`}
                    onClick={setGlobalLimitUsingLumio}
                  >
                    <img className="abl-lumio-icon" src={LUMIO_ICON_SRC} alt="" />
                    <span className="abl-chip-text abl-lumio-text">{formatMoney(lumioDailyLimit)}</span>
                  </div>
                </div>
              </div>

              {/* ===== allocation (node vW0xY1zA2b) ===== */}
              <div className="abl-section abl-allocation">
                <span className="abl-section-title"><Text text="Allocation" /></span>

                <div className="abl-bar-track">
                  <div className="abl-bar-seg abl-seg-blue" style={{ width: `${segWidth(allocation.alloc.web)}%` }} />
                  <div className="abl-bar-seg abl-seg-orange" style={{ width: `${segWidth(allocation.alloc.app)}%` }} />
                  <div
                    className="abl-bar-seg abl-seg-magenta"
                    style={{ width: `${segWidth(allocation.alloc.crypto)}%` }}
                  />
                  <div
                    className="abl-bar-seg abl-seg-yellow"
                    style={{ width: `${segWidth(allocation.alloc.video)}%` }}
                  />
                  {allocation.unallocated > 0 && (
                    <div
                      className="abl-bar-seg abl-not-allocated"
                      style={{ width: `${segWidth(allocation.unallocated)}%` }}
                    />
                  )}
                </div>

                <div className="abl-legend-row">
                  <div className="abl-legend-item">
                    <div className="abl-legend-dot abl-dot-blue" />
                    <span className="abl-legend-text"><Text text="Web ${{amount}}" placeholders={{ amount: formatAmountDisplay(String(allocation.alloc.web ?? 0)) }} /></span>
                  </div>
                  {/* Gated channels are omitted from the legend (and their bar segment is
                      $0-wide, so it collapses) — only live channels are listed. */}
                  {isAcquisitionChannelEnabled('app') && (
                    <div className="abl-legend-item">
                      <div className="abl-legend-dot abl-dot-orange" />
                      <span className="abl-legend-text"><Text text="App ${{amount}}" placeholders={{ amount: formatAmountDisplay(String(allocation.alloc.app ?? 0)) }} /></span>
                    </div>
                  )}
                  {isAcquisitionChannelEnabled('crypto') && (
                    <div className="abl-legend-item">
                      <div className="abl-legend-dot abl-dot-magenta" />
                      <span className="abl-legend-text"><Text text="Crypto ${{amount}}" placeholders={{ amount: formatAmountDisplay(String(allocation.alloc.crypto ?? 0)) }} /></span>
                    </div>
                  )}
                  {isAcquisitionChannelEnabled('video') && (
                    <div className="abl-legend-item">
                      <div className="abl-legend-dot abl-dot-yellow" />
                      <span className="abl-legend-text"><Text text="Video ${{amount}}" placeholders={{ amount: formatAmountDisplay(String(allocation.alloc.video ?? 0)) }} /></span>
                    </div>
                  )}
                  <div className="abl-legend-item">
                    <div className="abl-legend-dot" />
                    <span className="abl-legend-text"><Text text="Unallocated ${{amount}}" placeholders={{ amount: formatAmountDisplay(String(allocation.unallocated ?? 0)) }} /></span>
                  </div>
                </div>
              </div>

              {/* ===== per channel (node cD3eF4gH5i + ModuleList lePfeVBgmy) ===== */}
              <span className="abl-section-title abl-perchannel-title"><Text text="Per Channel" /></span>

              <div className="challels-list" style={{ width: '100%' }}>
                <div
                  className="channel-list-items"
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {channelRows.map((ch) => {
                    const allocated = allocation.alloc[ch.id] ?? 0;
                    // Live channels show real ad-spend + CPA; locked ones keep scaffold figures.
                    const fig = channelFigure(ch.id);

                    return (
                      <div key={ch.id} className="channel-list-item" style={{ width: '100%' }}>
                        <div
                          className={`abl-channel-card${ch.mode === 'lumio' ? ' lumio' : ''}${
                            ch.enabled ? '' : ' locked'
                          }`}
                        >
                          {/* channel header (node DPAbDPV1_J) */}
                          <div className={`abl-channel-header ${ch.id}`}>
                            <div className="abl-channel-icon-wrap">
                              <ChannelGlyph id={ch.id} className="abl-channel-icon" />
                            </div>
                            <div className="abl-channel-info">
                              <span className="abl-channel-name"><Text text={channelName(ch.name)} /></span>
                              <span className="abl-channel-stats">
                                {!fig || fig.cpaState === 'value' || fig.cpaState === 'error' ? (
                                  <Text
                                    text="{{cpa}} · ${{spent}} spent today"
                                    placeholders={{ cpa: fig?.cpaLabel ?? `$${ch.cpa} CPA`, spent: fig?.spent ?? ch.spent }}
                                  />
                                ) : fig.cpaState === 'pending' ? (
                                  <Text text="CPA pending · ${{spent}} spent today" placeholders={{ spent: fig.spent }} />
                                ) : (
                                  <Text text="${{spent}} spent today" placeholders={{ spent: fig.spent }} />
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Manual slider + Lumio strip share ONE grid cell
                              (`.abl-mode-stack`) so the per-channel card is the same
                              height in both modes: toggling Lumio on/off never shifts
                              the layout (the cell is always as tall as the strip). */}
                          <div className="abl-mode-stack">
                            {/* manual / default content (node 4hk8PTCaNK) */}
                            <div className="abl-default-content">
                              <div className="abl-channel-limit-row">
                                <span className="abl-limit-label"><Text text="Daily Limit" /></span>
                                <div className="abl-limit-value-wrap">
                                  <span className="abl-limit-amount">{formatMoney(allocated)}</span>
                                  <span className="abl-limit-unit"><Text text="/day" /></span>
                                </div>
                              </div>

                              {/* slider (node jwr-3bTkd-) */}
                              <div className="abl-channel-slider">
                                <div className="abl-slider-track" style={{ width: `${ch.maxSliderLenght}%` }}>
                                  <div
                                    className={`abl-slider-track-colored ${ch.id}`}
                                    style={{ width: `${ch.progress}%` }}
                                  />
                                  <div
                                    className="abl-slider-track-thumb"
                                    style={{ left: thumbLeft(ch.progress, ch.maxSliderLenght) }}
                                  />
                                  <input
                                    id={`${ch.id}-channel-slider`}
                                    className="abl-range-input"
                                    type="range"
                                    min={0}
                                    max={ch.maxRange}
                                    step={1}
                                    value={ch.manualLimit}
                                    data-channelid={ch.id}
                                    data-value={ch.manualLimit}
                                    onChange={setChannelLimit}
                                    disabled={!ch.enabled}
                                    style={{ ['--range-progress']: `${ch.progress}%` } as React.CSSProperties}
                                  />
                                  <div className="abl-slider-track-default" style={{ width: `${100 - ch.progress}%` }} />
                                </div>
                              </div>
                            </div>

                            {/* lumio content (node 6420:23801): single #101012 row, label left, auto-allocated amount right */}
                            <div className="abl-lumio-content">
                              <span className="abl-lumio-label"><Text text="Automatic budget allocation" /></span>
                              <div className="abl-limit-value-wrap">
                                <span className="abl-limit-amount">{formatMoney(allocated)}</span>
                                <span className="abl-limit-unit"><Text text="/day" /></span>
                              </div>
                            </div>
                          </div>

                          <div className="abl-toggle-row">
                            <LumioSparkle className="abl-lumio-icon" />
                            <span className="abl-toggle-text"><Text text="Allow Lumio to manage daily budget" /></span>
                            <div className="abl-toggle-switch" data-channelid={ch.id} onClick={toggleChannelMode}>
                              <div className="abl-toggle-knob" />
                            </div>
                          </div>

                          {/* Gated channel (release feature flag): frost the whole row under a
                              padlock so its limit controls read as "{name} · Unlocking soon...". */}
                          {ch.enabled ? null : (
                            // Match the channel-row header (icon 24 · name 16px · gap 10 · padding 14).
                            <LockVeil
                              radius={16}
                              name={channelName(ch.name)}
                              icon={BUDGET_CHANNEL_ICON[ch.id]}
                              iconSize={24}
                              fontSize={16}
                              headerGap={10}
                              pad={14}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

            </div>
          )}
        </div>
      </Sheet>

      {/* top-up confirmation: funds move from the main wallet (backend-handled transfer).
          Portalled to <body> — the same target as the Overlay — so this sits in the
          body-level stacking context as a sibling of the Overlay backdrop, where its
          higher z-index (2200 > the Overlay's 2000) actually wins. Rendered inline in
          the page DOM it loses regardless of z-index, because the content area's own
          stacking context sits beneath the body-level Overlay portal. The
          `.ad-budget-limits` wrapper keeps its scoped styles reachable. */}
      {confirmOpen &&
        createPortal(
          <div className="ad-budget-limits">
            <div className="abl-confirm" onClick={onConfirmBackdrop}>
          <div
            ref={confirmRef}
            className="abl-confirm-card"
            role="dialog"
            aria-modal="true"
            aria-label={t('Confirm top-up')}
            tabIndex={-1}
          >
            <span className="abl-confirm-title"><Text text="Confirm top-up" /></span>

            <div className="abl-confirm-rows">
              <div className="abl-confirm-row">
                <span className="abl-confirm-row-label">
                  <span className="abl-confirm-row-caption"><Text text="From" /></span>
                  <span className="abl-confirm-row-main"><Text text="Main wallet" /></span>
                </span>
                <span className="abl-confirm-row-value">
                  {walletLoaded && !walletError ? walletMoney(mainWalletBalance) : '–'}
                </span>
              </div>
              <div className="abl-confirm-row">
                <span className="abl-confirm-row-label">
                  <span className="abl-confirm-row-caption"><Text text="To" /></span>
                  <span className="abl-confirm-row-main">
                    {brandName ? (
                      <Text text="{{brandName}} Ads Budget" placeholders={{ brandName }} />
                    ) : (
                      <Text text="Ads Budget" />
                    )}
                  </span>
                </span>
                <span className="abl-confirm-row-value">{formatMoney(tuAmount)}</span>
              </div>
              {/* Resulting ad budget after the top-up — a total row set off by a hairline. */}
              <div className="abl-confirm-row abl-confirm-row-total">
                <span className="abl-confirm-total-label"><Text text="Updated Ads Budget" /></span>
                <span className="abl-confirm-row-value">{formatMoney((acquisitionBudgetValue ?? 0) + tuAmount)}</span>
              </div>
            </div>

            {/* Defence in depth: the tab's CTA already swaps to the deposit when funds are
                short, so this only fires if the balance drops while the dialog is open. */}
            {funding.status === 'short' && (
              <span className="abl-confirm-warn"><Text text="Not enough in your main wallet for this top-up." /></span>
            )}
            {funding.status === 'unknown' && (
              <span className="abl-confirm-warn">
                <Text text="We couldn't read your main wallet balance, so this top-up can't be confirmed yet." />
              </span>
            )}

            <div className="abl-confirm-actions">
              <Button label={t('Cancel')} variant="outline" onClick={cancelTopup} />
              <Button
                label={t('Top up ${{tuAmount}}', { tuAmount: formatAmountDisplay(String(tuAmount)) })}
                variant="primary"
                onClick={confirmTopup}
                disabled={!canConfirmTopup}
              />
            </div>
          </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Main-wallet deposit (the wallet page's Stripe checkout, reused as-is). It portals to
          <body> after this Overlay, so it stacks above it. `onSuccess` fires when Stripe confirms
          the charge, which starts the balance watch above. Denominated in the MAIN wallet's own
          currency, the same one the balance and the shortfall are quoted in. */}
      {depositOpen && (
        <DepositModal currency={walletView.currency} onClose={closeDeposit} onSuccess={onDeposited} />
      )}
    </>
  );
};

export default AdBudgetLimit;
